import React, { useContext, useEffect, useMemo, useState } from 'react';
import { SalesHistoryContext } from '../SalesHistoryContext';
import { useAuth } from '../AuthContext';
import { apiJson } from '../apiClient';
import { normalizeRole } from '../utils/userLocale';
import { getLocalDateKey } from '../utils/reportDates';
import { buildFinancialReport, formatTzs } from '../utils/financialReportData';
import { isAutomotiveBusiness, isBrickFactoryUser } from '../utils/businessType';
import { hasTenantModule } from '../utils/tenantModules';
import './BusinessFinancialReportPage.css';

const ReportsPage = ({ user, onNavigate }) => {
  const { user: authUser } = useAuth();
  const currentUser = user || authUser;
  const isOwner = normalizeRole(currentUser?.role) === 'owner';
  const { salesHistory } = useContext(SalesHistoryContext);
  const today = getLocalDateKey();
  const [expenses, setExpenses] = useState([]);
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const tasks = [apiJson('/expenses').catch(() => ({ expenses: [] }))];
        if (isOwner) tasks.push(apiJson('/orders').catch(() => ({ orders: [] })));
        else tasks.push(apiJson('/my-orders').catch(() => ({ orders: [] })));
        const [expData, ordData] = await Promise.all(tasks);
        if (!mounted) return;
        setExpenses(Array.isArray(expData.expenses) ? expData.expenses : []);
        setOrders(Array.isArray(ordData.orders) ? ordData.orders : []);
      } catch {
        if (!mounted) return;
        setExpenses([]);
        setOrders([]);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [isOwner]);

  const todayReport = useMemo(
    () =>
      buildFinancialReport({
        sales: salesHistory,
        expenses,
        orders,
        fromDate: today,
        toDate: today,
        user: currentUser,
      }),
    [salesHistory, expenses, orders, today, currentUser]
  );

  const showPrinting = hasTenantModule(currentUser, 'printing') && !isBrickFactoryUser(currentUser) && !isAutomotiveBusiness(currentUser);

  return (
    <div className="fin-report page-bleed">
      <header className="fin-report__hero">
        <div>
          <h1>Reports</h1>
          <p>
            {isAutomotiveBusiness(currentUser)
              ? 'Muhtasari wa mauzo, manunuzi, matumizi na faida — kwa spare parts shop yako.'
              : isBrickFactoryUser(currentUser)
                ? 'Factory sales, purchases, expenses and profit summaries.'
                : 'Business sales, purchases, expenses and profit — clear summaries for any period.'}
          </p>
        </div>
      </header>

      <h2 className="fin-report__hub-title">Today at a glance</h2>
      <div className="fin-report__kpis fin-report__kpis--hub">
        <article className="fin-report__kpi fin-report__kpi--sales">
          <span className="fin-report__kpi-label">Today sales</span>
          <strong className="fin-report__kpi-value">{formatTzs(todayReport.salesRevenue)}</strong>
          <span className="fin-report__kpi-meta">{todayReport.salesCount} transactions</span>
        </article>
        <article className="fin-report__kpi fin-report__kpi--purchases">
          <span className="fin-report__kpi-label">Today purchases</span>
          <strong className="fin-report__kpi-value">{formatTzs(todayReport.purchasesTotal)}</strong>
          <span className="fin-report__kpi-meta">{todayReport.purchasesCount} orders</span>
        </article>
        <article className="fin-report__kpi fin-report__kpi--expenses">
          <span className="fin-report__kpi-label">Today expenses</span>
          <strong className="fin-report__kpi-value">{formatTzs(todayReport.expensesTotal)}</strong>
        </article>
        <article
          className={`fin-report__kpi fin-report__kpi--profit${todayReport.netProfit < 0 ? ' fin-report__kpi--loss' : ''}`}
        >
          <span className="fin-report__kpi-label">Today net profit</span>
          <strong className="fin-report__kpi-value">{formatTzs(todayReport.netProfit)}</strong>
        </article>
      </div>

      <h2 className="fin-report__hub-title">Choose a report</h2>
      <div className="fin-report__hub-grid">
        <button type="button" className="fin-report__hub-card fin-report__hub-card--primary" onClick={() => onNavigate('report-financial')}>
          <h3>Business summary</h3>
          <p>Sales, purchases, expenses &amp; profit — day, week, month or custom range.</p>
        </button>
        <button type="button" className="fin-report__hub-card" onClick={() => onNavigate('report-daily')}>
          <h3>Daily report</h3>
          <p>Full breakdown for one selected day.</p>
        </button>
        <button type="button" className="fin-report__hub-card" onClick={() => onNavigate('report-weekly')}>
          <h3>Weekly report</h3>
          <p>Week totals with sales, purchases and expenses.</p>
        </button>
        <button type="button" className="fin-report__hub-card" onClick={() => onNavigate('report-monthly')}>
          <h3>Monthly report</h3>
          <p>Month summary — revenue, costs and net profit.</p>
        </button>
        <button type="button" className="fin-report__hub-card" onClick={() => onNavigate('report-stock')}>
          <h3>Stock report</h3>
          <p>Inventory valuation and low stock alerts.</p>
        </button>
        {showPrinting ? (
          <button type="button" className="fin-report__hub-card" onClick={() => onNavigate('report-printing')}>
            <h3>Printing report</h3>
            <p>Print &amp; copy jobs only (if you use printing module).</p>
          </button>
        ) : null}
        {hasTenantModule(currentUser, 'cashbook') ? (
          <button type="button" className="fin-report__hub-card" onClick={() => onNavigate('report-wakala')}>
            <h3>Agent cash report</h3>
            <p>Wakala / mobile money cashbook.</p>
          </button>
        ) : null}
      </div>
    </div>
  );
};

export default ReportsPage;
